import Model, { attr } from '@ember-data/model';
import { apiAction } from '@mainmatter/ember-api-actions';
import { REQUEST_TIMEOUT_MS, withRequestDeadline } from 'garaje/utils/draft-documents';

interface DraftDocumentDocument {
  data: {
    id: string;
    type: 'draft-documents';
    attributes: Record<string, unknown>;
  };
}

export type DraftDocumentStatus = 'pending' | 'processing' | 'completed';

/**
 * Free-form map of opaque display label → ISO-8601 date (a COI yields one key
 * per coverage). Parse failure = `completed` with an empty map (no `failed` status).
 */
export type DraftDocumentFieldValues = Record<string, string>;

export default class DraftDocument extends Model {
  // defaultValue keeps the declared (non-null) type honest for not-yet-loaded records.
  @attr('string', { defaultValue: 'pending' }) declare status: DraftDocumentStatus;

  @attr('immutable', { defaultValue: () => ({}) })
  declare fieldValues: DraftDocumentFieldValues;

  @attr('string', { defaultValue: null }) declare templateId: string | null;
  @attr('string', { defaultValue: null }) declare locationId: string | null;

  get isProcessing(): boolean {
    return this.status === 'processing';
  }

  get isCompleted(): boolean {
    return this.status === 'completed';
  }

  get hasFieldValues(): boolean {
    return Object.keys(this.fieldValues ?? {}).length > 0;
  }

  /**
   * PATCH operator-edited field values (expiration dates) onto the draft.
   *
   * Uses `apiAction` (no `path`) to PATCH the record with ONLY `field-values`.
   * A full `save()` would serialize every declared attribute (`status`,
   * `template-id`, `location-id`) too — `field-values` is the only editable
   * draft attribute, and a strict API rejects the rest on update. The verbatim
   * `data` body keeps that "only field-values" guarantee structural.
   */
  async updateFieldValues(
    fieldValues: DraftDocumentFieldValues,
    { timeoutMs = REQUEST_TIMEOUT_MS }: { timeoutMs?: number } = {},
  ): Promise<this> {
    const payload = await withRequestDeadline(
      apiAction<DraftDocumentDocument>(this, {
        method: 'PATCH',
        data: { data: { type: 'draft-documents', id: this.id, attributes: { 'field-values': fieldValues } } },
      }),
      'Draft document field-values update',
      timeoutMs,
    );

    this.store.pushPayload('draft-document', payload);

    return this;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'draft-document': DraftDocument;
  }
}
